import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  FaBan,
  FaCheckCircle,
  FaEdit,
  FaEraser,
  FaFilter,
  FaFolderOpen,
  FaPlus,
  FaSave,
  FaSearch,
  FaTags,
  FaTimes,
  FaTrash,
  FaUndo,
} from "react-icons/fa";

const EMPTY_FORM = {
  id: null,
  rubrique_id: "",
  sub_rubrique_id: "",
  old_code: "",
  code: "",
  libelle: "",
  ordre: "",
  is_active: true,
};

const inputClass =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100";

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </div>
      {children}
    </label>
  );
}

function isActive(row) {
  return (
    row?.is_active === true ||
    row?.is_active === 1 ||
    row?.is_active === "1"
  );
}

function getRowRubriqueId(row) {
  return row?.rubrique_id || row?.rubrique?.id || "";
}

function getRowSubRubriqueId(row) {
  return row?.sub_rubrique_id || row?.sub_rubrique?.id || "";
}

function getRubriqueCode(row) {
  return (
    row?.rubrique?.code ||
    row?.rubrique_code ||
    String(row?.code || "").match(/^[A-Z]+/)?.[0] ||
    "AUTRE"
  );
}

function getNumericOrder(row) {
  if (row?.ordre !== null && row?.ordre !== undefined && row?.ordre !== "") {
    const n = Number(row.ordre);
    if (Number.isFinite(n)) return n;
  }

  const code = String(row?.code || "");
  const match = code.match(/\d+/);

  return match ? Number(match[0]) : 999999;
}

function getSubRubriqueLabel(row) {
  return row?.sub_rubrique?.libelle || "";
}

function compareBudgetCodes(a, b) {
  const rubriqueOrderA = Number(a?.rubrique?.ordre ?? 9999);
  const rubriqueOrderB = Number(b?.rubrique?.ordre ?? 9999);

  if (rubriqueOrderA !== rubriqueOrderB) {
    return rubriqueOrderA - rubriqueOrderB;
  }

  const subOrderA = Number(a?.sub_rubrique?.ordre ?? 9999);
  const subOrderB = Number(b?.sub_rubrique?.ordre ?? 9999);

  if (subOrderA !== subOrderB) {
    return subOrderA - subOrderB;
  }

  const orderA = getNumericOrder(a);
  const orderB = getNumericOrder(b);

  if (orderA !== orderB) {
    return orderA - orderB;
  }

  return String(a?.code || "").localeCompare(String(b?.code || ""), "fr");
}

function searchText(row) {
  return [
    row?.old_code,
    row?.code,
    row?.libelle,
    row?.rubrique?.code,
    row?.rubrique?.libelle,
    row?.sub_rubrique?.code,
    row?.sub_rubrique?.libelle,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

export default function BudgetCodesManagerDrawer({
  open,
  onClose,
  budgetCodes = [],
  budgetRubriques = [],
  loading = false,
  onCreateCode,
  onUpdateCode,
  onDeleteCode,
  onChanged,
}) {
  const [filters, setFilters] = useState({
    q: "",
    rubrique_id: "",
    sub_rubrique_id: "",
    active: "all",
  });

  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState("");

  useEffect(() => {
    if (!open) {
      setForm(EMPTY_FORM);
      setFilters({
        q: "",
        rubrique_id: "",
        sub_rubrique_id: "",
        active: "all",
      });
      setSaving(false);
      setBusyId("");
    }
  }, [open]);

  const rubriquesOptions = useMemo(() => {
    return [...(budgetRubriques || [])].sort((a, b) => {
      const ordreA = Number(a?.ordre ?? 9999);
      const ordreB = Number(b?.ordre ?? 9999);

      if (ordreA !== ordreB) return ordreA - ordreB;

      return String(a?.code || "").localeCompare(String(b?.code || ""), "fr");
    });
  }, [budgetRubriques]);

  const selectedFormRubrique = useMemo(() => {
    if (!form.rubrique_id) return null;

    return rubriquesOptions.find(
      (rubrique) => String(rubrique.id) === String(form.rubrique_id),
    );
  }, [rubriquesOptions, form.rubrique_id]);

  const selectedFilterRubrique = useMemo(() => {
    if (!filters.rubrique_id) return null;

    return rubriquesOptions.find(
      (rubrique) => String(rubrique.id) === String(filters.rubrique_id),
    );
  }, [rubriquesOptions, filters.rubrique_id]);

  const formSubRubriques = useMemo(() => {
    return selectedFormRubrique?.sub_rubriques || [];
  }, [selectedFormRubrique]);

  const filterSubRubriques = useMemo(() => {
    return selectedFilterRubrique?.sub_rubriques || [];
  }, [selectedFilterRubrique]);

  const sortedCodes = useMemo(() => {
    return [...(budgetCodes || [])].sort(compareBudgetCodes);
  }, [budgetCodes]);

  const filteredCodes = useMemo(() => {
    const q = filters.q.trim().toLowerCase();
    const active = filters.active;

    return sortedCodes.filter((row) => {
      if (filters.rubrique_id) {
        const rowRubriqueId = getRowRubriqueId(row);
        const fallbackCode = getRubriqueCode(row);
        const selectedRubriqueCode = selectedFilterRubrique?.code;

        const sameRubrique =
          String(rowRubriqueId) === String(filters.rubrique_id) ||
          String(fallbackCode) === String(selectedRubriqueCode);

        if (!sameRubrique) return false;
      }

      if (filters.sub_rubrique_id) {
        const rowSubRubriqueId = getRowSubRubriqueId(row);

        if (String(rowSubRubriqueId) !== String(filters.sub_rubrique_id)) {
          return false;
        }
      }

      if (active === "active" && !isActive(row)) return false;
      if (active === "inactive" && isActive(row)) return false;

      if (q && !searchText(row).includes(q)) return false;

      return true;
    });
  }, [sortedCodes, filters, selectedFilterRubrique]);

  const stats = useMemo(() => {
    const total = budgetCodes.length;
    const active = budgetCodes.filter(isActive).length;
    const inactive = total - active;
    const rubriques = new Set(
      budgetCodes.map((row) => getRowRubriqueId(row) || getRubriqueCode(row)),
    ).size;

    return {
      total,
      active,
      inactive,
      rubriques,
    };
  }, [budgetCodes]);

  if (!open) return null;

  const setFilter = (field, value) => {
    setFilters((prev) => {
      if (field === "rubrique_id") {
        return {
          ...prev,
          rubrique_id: value,
          sub_rubrique_id: "",
        };
      }

      return {
        ...prev,
        [field]: value,
      };
    });
  };

  const setFormField = (field, value) => {
    setForm((prev) => {
      if (field === "rubrique_id") {
        return {
          ...prev,
          rubrique_id: value,
          sub_rubrique_id: "",
        };
      }

      return {
        ...prev,
        [field]: value,
      };
    });
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
  };

  const editRow = (row) => {
    setForm({
      id: row.id,
      rubrique_id: getRowRubriqueId(row) || "",
      sub_rubrique_id: getRowSubRubriqueId(row) || "",
      old_code: row.old_code || "",
      code: row.code || "",
      libelle: row.libelle || "",
      ordre:
        row.ordre === null || row.ordre === undefined || row.ordre === ""
          ? getNumericOrder(row)
          : row.ordre,
      is_active: isActive(row),
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const rubrique_id = form.rubrique_id ? Number(form.rubrique_id) : null;
    const sub_rubrique_id = form.sub_rubrique_id
      ? Number(form.sub_rubrique_id)
      : null;

    const code = String(form.code || "").trim().toUpperCase();
    const libelle = String(form.libelle || "").trim();
    const old_code = String(form.old_code || "").trim() || null;

    if (!rubrique_id) {
      toast.error("La rubrique est obligatoire.");
      return;
    }

    if (!code) {
      toast.error("Le code budgétaire est obligatoire.");
      return;
    }

    if (!libelle) {
      toast.error("Le libellé est obligatoire.");
      return;
    }

    const ordre =
      form.ordre === "" || form.ordre === null || form.ordre === undefined
        ? 0
        : Number(form.ordre);

    const payload = {
      rubrique_id,
      sub_rubrique_id,
      old_code,
      code,
      libelle,
      ordre: Number.isFinite(ordre) ? ordre : 0,
      is_active: Boolean(form.is_active),
    };

    try {
      setSaving(true);

      if (form.id) {
        await onUpdateCode(form.id, payload);
        toast.success("Code budgétaire mis à jour.");
      } else {
        await onCreateCode(payload);
        toast.success("Code budgétaire créé.");
      }

      resetForm();
      await onChanged?.();
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.message ||
          "Impossible d’enregistrer le code budgétaire.",
      );
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (row) => {
    if (!row?.id) return;

    const nextActive = !isActive(row);

    const rubrique_id = getRowRubriqueId(row);
    const sub_rubrique_id = getRowSubRubriqueId(row) || null;

    if (!rubrique_id) {
      toast.error(
        "Impossible de modifier ce code : aucune rubrique n'est associée.",
      );
      return;
    }

    try {
      setBusyId(`toggle-${row.id}`);

      await onUpdateCode(row.id, {
        rubrique_id: Number(rubrique_id),
        sub_rubrique_id: sub_rubrique_id ? Number(sub_rubrique_id) : null,
        code: row.code,
        libelle: row.libelle,
        old_code: row.old_code || null,
        ordre:
          row.ordre === null || row.ordre === undefined
            ? getNumericOrder(row)
            : Number(row.ordre),
        is_active: nextActive,
      });

      toast.success(nextActive ? "Code réactivé." : "Code désactivé.");
      await onChanged?.();
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.message ||
          "Impossible de modifier le statut du code.",
      );
    } finally {
      setBusyId("");
    }
  };

  const deleteRow = async (row) => {
    if (!row?.id) return;

    const ok = window.confirm(
      `Supprimer définitivement le code ${row.code} ?\n\nS'il est déjà utilisé dans une fiche de frais, il vaut mieux le désactiver plutôt que le supprimer.`,
    );

    if (!ok) return;

    try {
      setBusyId(`delete-${row.id}`);

      await onDeleteCode(row.id);

      toast.success("Code budgétaire supprimé.");

      if (form.id === row.id) {
        resetForm();
      }

      await onChanged?.();
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.message ||
          "Impossible de supprimer ce code. Il est peut-être déjà utilisé dans une fiche.",
      );
    } finally {
      setBusyId("");
    }
  };

  return (
    <div className="fixed inset-0 z-[95]">
      <div
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-[1px]"
        onClick={onClose}
      />

      <aside className="absolute right-0 top-0 h-full w-full max-w-6xl overflow-y-auto bg-slate-100 shadow-2xl">
        <div className="sticky top-0 z-10 border-b border-slate-200 bg-white/95 px-5 py-4 backdrop-blur">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="inline-flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-900 text-white">
                <FaTags className="text-xl" />
              </div>

              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Administration
                </div>
                <h2 className="mt-1 text-2xl font-black text-slate-900">
                  Codes budgétaires
                </h2>
                <p className="mt-1 max-w-3xl text-sm text-slate-500">
                  Crée, filtre, modifie, désactive ou supprime les codes par
                  rubrique et sous-rubrique.
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-50"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        <div className="space-y-5 p-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-sm font-medium text-slate-500">
                Total codes
              </div>
              <div className="mt-2 text-3xl font-black text-slate-900">
                {stats.total}
              </div>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-sm font-medium text-slate-500">
                Codes actifs
              </div>
              <div className="mt-2 text-3xl font-black text-emerald-700">
                {stats.active}
              </div>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-sm font-medium text-slate-500">
                Codes inactifs
              </div>
              <div className="mt-2 text-3xl font-black text-rose-700">
                {stats.inactive}
              </div>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-sm font-medium text-slate-500">
                Rubriques
              </div>
              <div className="mt-2 text-3xl font-black text-sky-700">
                {stats.rubriques}
              </div>
            </div>
          </div>

          <form
            onSubmit={handleSubmit}
            className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm"
          >
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-lg font-black text-slate-900">
                  {form.id ? `Modifier ${form.code}` : "Créer un code"}
                </div>
                <div className="text-sm text-slate-500">
                  Choisis d’abord la rubrique, puis la sous-rubrique si elle
                  existe.
                </div>
              </div>

              {form.id ? (
                <button
                  type="button"
                  onClick={resetForm}
                  className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                >
                  <FaUndo />
                  Nouveau code
                </button>
              ) : null}
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-6">
              <Field label="Rubrique">
                <select
                  className={inputClass}
                  value={form.rubrique_id}
                  onChange={(e) => setFormField("rubrique_id", e.target.value)}
                >
                  <option value="">Choisir une rubrique</option>
                  {rubriquesOptions.map((rubrique) => (
                    <option key={rubrique.id} value={rubrique.id}>
                      {rubrique.code} · {rubrique.libelle}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Sous-rubrique">
                <select
                  className={inputClass}
                  value={form.sub_rubrique_id}
                  disabled={!form.rubrique_id}
                  onChange={(e) =>
                    setFormField("sub_rubrique_id", e.target.value)
                  }
                >
                  <option value="">Sans sous-rubrique</option>
                  {formSubRubriques.map((subRubrique) => (
                    <option key={subRubrique.id} value={subRubrique.id}>
                      {subRubrique.code
                        ? `${subRubrique.code} · ${subRubrique.libelle}`
                        : subRubrique.libelle}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Ancien code">
                <input
                  type="text"
                  className={inputClass}
                  placeholder="M11, M24..."
                  value={form.old_code}
                  onChange={(e) => setFormField("old_code", e.target.value)}
                />
              </Field>

              <Field label="Nouveau code">
                <input
                  type="text"
                  className={inputClass}
                  placeholder="SPOR101"
                  value={form.code}
                  onChange={(e) =>
                    setFormField("code", e.target.value.toUpperCase())
                  }
                />
              </Field>

              <div className="md:col-span-2 xl:col-span-2">
                <Field label="Libellé">
                  <input
                    type="text"
                    className={inputClass}
                    placeholder="PE Limoges"
                    value={form.libelle}
                    onChange={(e) => setFormField("libelle", e.target.value)}
                  />
                </Field>
              </div>

              <Field label="Ordre">
                <input
                  type="number"
                  className={inputClass}
                  placeholder="101"
                  value={form.ordre}
                  onChange={(e) => setFormField("ordre", e.target.value)}
                />
              </Field>

              <Field label="Statut">
                <select
                  className={inputClass}
                  value={form.is_active ? "1" : "0"}
                  onChange={(e) =>
                    setFormField("is_active", e.target.value === "1")
                  }
                >
                  <option value="1">Actif</option>
                  <option value="0">Inactif</option>
                </select>
              </Field>
            </div>

            <div className="mt-5 flex flex-wrap gap-3">
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {form.id ? <FaSave /> : <FaPlus />}
                {saving
                  ? "Enregistrement..."
                  : form.id
                    ? "Enregistrer les modifications"
                    : "Créer le code"}
              </button>

              <button
                type="button"
                onClick={resetForm}
                disabled={saving}
                className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <FaEraser />
                Réinitialiser
              </button>
            </div>
          </form>

          <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center gap-3">
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-sky-50 text-sky-700 ring-1 ring-sky-200">
                <FaFilter />
              </div>
              <div>
                <div className="text-lg font-black text-slate-900">
                  Recherche et filtres
                </div>
                <div className="text-sm text-slate-500">
                  {filteredCodes.length} code(s) affiché(s)
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
              <Field label="Recherche">
                <div className="relative">
                  <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    className={`${inputClass} pl-11`}
                    placeholder="SPOR101, Limoges, M11..."
                    value={filters.q}
                    onChange={(e) => setFilter("q", e.target.value)}
                  />
                </div>
              </Field>

              <Field label="Rubrique">
                <select
                  className={inputClass}
                  value={filters.rubrique_id}
                  onChange={(e) => setFilter("rubrique_id", e.target.value)}
                >
                  <option value="">Toutes les rubriques</option>
                  {rubriquesOptions.map((rubrique) => (
                    <option key={rubrique.id} value={rubrique.id}>
                      {rubrique.code} · {rubrique.libelle}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Sous-rubrique">
                <select
                  className={inputClass}
                  value={filters.sub_rubrique_id}
                  disabled={!filters.rubrique_id}
                  onChange={(e) =>
                    setFilter("sub_rubrique_id", e.target.value)
                  }
                >
                  <option value="">Toutes les sous-rubriques</option>
                  {filterSubRubriques.map((subRubrique) => (
                    <option key={subRubrique.id} value={subRubrique.id}>
                      {subRubrique.code
                        ? `${subRubrique.code} · ${subRubrique.libelle}`
                        : subRubrique.libelle}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Statut">
                <select
                  className={inputClass}
                  value={filters.active}
                  onChange={(e) => setFilter("active", e.target.value)}
                >
                  <option value="all">Tous</option>
                  <option value="active">Actifs uniquement</option>
                  <option value="inactive">Inactifs uniquement</option>
                </select>
              </Field>
            </div>
          </div>

          {loading ? (
            <div className="rounded-3xl border border-slate-200 bg-white p-8 text-center text-slate-500 shadow-sm">
              Chargement des codes budgétaires…
            </div>
          ) : !filteredCodes.length ? (
            <div className="rounded-3xl border border-dashed border-slate-300 bg-white p-10 text-center shadow-sm">
              <div className="mx-auto mb-4 inline-flex h-16 w-16 items-center justify-center rounded-3xl bg-slate-100 text-slate-500">
                <FaFolderOpen className="text-2xl" />
              </div>
              <div className="text-lg font-bold text-slate-900">
                Aucun code trouvé
              </div>
              <div className="mt-2 text-sm text-slate-500">
                Modifie les filtres ou crée un nouveau code.
              </div>
            </div>
          ) : (
            <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold">
                        Rubrique
                      </th>
                      <th className="px-4 py-3 text-left font-semibold">
                        Sous-rubrique
                      </th>
                      <th className="px-4 py-3 text-left font-semibold">
                        Ancien
                      </th>
                      <th className="px-4 py-3 text-left font-semibold">
                        Code
                      </th>
                      <th className="px-4 py-3 text-left font-semibold">
                        Libellé
                      </th>
                      <th className="px-4 py-3 text-left font-semibold">
                        Statut
                      </th>
                      <th className="px-4 py-3 text-right font-semibold">
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredCodes.map((row) => {
                      const active = isActive(row);
                      const editing = form.id === row.id;

                      return (
                        <tr
                          key={row.id}
                          className={`border-t border-slate-100 transition ${
                            editing ? "bg-sky-50/70" : "bg-white"
                          } hover:bg-slate-50`}
                        >
                          <td className="px-4 py-4">
                            <span className="inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-700 ring-1 ring-slate-200">
                              {row?.rubrique
                                ? `${row.rubrique.code} · ${row.rubrique.libelle}`
                                : getRubriqueCode(row)}
                            </span>
                          </td>

                          <td className="px-4 py-4 text-slate-500">
                            {getSubRubriqueLabel(row) || "—"}
                          </td>

                          <td className="px-4 py-4 text-slate-500">
                            {row.old_code || "—"}
                          </td>

                          <td className="px-4 py-4 font-black text-slate-900">
                            {row.code}
                          </td>

                          <td className="px-4 py-4 text-slate-700">
                            {row.libelle}
                          </td>

                          <td className="px-4 py-4">
                            {active ? (
                              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-800 ring-1 ring-emerald-200">
                                <FaCheckCircle />
                                Actif
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1.5 rounded-full bg-rose-100 px-2.5 py-1 text-xs font-semibold text-rose-800 ring-1 ring-rose-200">
                                <FaBan />
                                Inactif
                              </span>
                            )}
                          </td>

                          <td className="px-4 py-4">
                            <div className="flex justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => editRow(row)}
                                className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 font-semibold text-slate-700 transition hover:bg-slate-50"
                              >
                                <FaEdit />
                                Modifier
                              </button>

                              <button
                                type="button"
                                disabled={!!busyId}
                                onClick={() => toggleActive(row)}
                                className={`inline-flex items-center gap-2 rounded-2xl px-3 py-2 font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-60 ${
                                  active
                                    ? "bg-amber-600 hover:bg-amber-700"
                                    : "bg-emerald-600 hover:bg-emerald-700"
                                }`}
                              >
                                {active ? <FaBan /> : <FaCheckCircle />}
                                {busyId === `toggle-${row.id}`
                                  ? "..."
                                  : active
                                    ? "Désactiver"
                                    : "Activer"}
                              </button>

                              <button
                                type="button"
                                disabled={!!busyId}
                                onClick={() => deleteRow(row)}
                                className="inline-flex items-center gap-2 rounded-2xl bg-rose-600 px-3 py-2 font-semibold text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60"
                              >
                                <FaTrash />
                                {busyId === `delete-${row.id}`
                                  ? "..."
                                  : "Supprimer"}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}